import { Truck } from '../types/Truck';
import { Expense } from '../types/Expense';
import { Deposit } from '../types/Deposit';
import { ServiceSchedule, ServiceRecord } from '../types/Service';
import { Job } from '../types/Job';
import { UserProfile, DriverAllowlistEntry } from '../types/UserProfile';

type FirestoreTimestamp = { toDate: () => Date };
type AnyDoc = { id: string; data: () => any };

// Tolerant timestamp coercion: docs seeded by hand (e.g. an allowlist entry
// created in the Firebase console) may omit createdAt or store a plain value,
// which must not crash a whole collection read.
function ts(value: FirestoreTimestamp | Date | null | undefined): Date {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (typeof (value as FirestoreTimestamp).toDate === 'function') {
    return (value as FirestoreTimestamp).toDate();
  }
  return new Date();
}

export function mapTruckDoc(doc: AnyDoc): Truck {
  const d = doc.data();
  return {
    ...d,
    id: doc.id,
    createdAt: ts(d.createdAt),
    updatedAt: ts(d.updatedAt),
  } as Truck;
}

export function mapExpenseDoc(doc: AnyDoc): Expense {
  const d = doc.data();
  return {
    ...d,
    id: doc.id,
    createdAt: ts(d.createdAt),
    updatedAt: ts(d.updatedAt),
  } as Expense;
}

export function mapDepositDoc(doc: AnyDoc): Deposit {
  const d = doc.data();
  return {
    ...d,
    id: doc.id,
    depositDate: ts(d.depositDate),
    createdAt: ts(d.createdAt),
    updatedAt: ts(d.updatedAt),
  } as Deposit;
}

export function mapServiceScheduleDoc(doc: AnyDoc): ServiceSchedule {
  const d = doc.data();
  return {
    ...d,
    id: doc.id,
    nextDueDate: ts(d.nextDueDate),
    createdAt: ts(d.createdAt),
    updatedAt: ts(d.updatedAt),
  } as ServiceSchedule;
}

export function mapServiceRecordDoc(doc: AnyDoc): ServiceRecord {
  const d = doc.data();
  return {
    ...d,
    id: doc.id,
    performedDate: ts(d.performedDate),
    createdAt: ts(d.createdAt),
  } as ServiceRecord;
}

export function mapJobDoc(doc: AnyDoc): Job {
  const d = doc.data();
  return {
    ...d,
    id: doc.id,
    // Jobs created before the intake/revenue split have no intakeAmount; their
    // amount held the negotiated price. Backfill so display code never sees
    // undefined (formatAmount would throw on it).
    intakeAmount: d.intakeAmount ?? d.amount ?? 0,
    createdAt: ts(d.createdAt),
    updatedAt: ts(d.updatedAt),
    ...(d.completedAt ? { completedAt: ts(d.completedAt) } : {}),
  } as Job;
}

export function mapUserProfileDoc(doc: AnyDoc): UserProfile {
  const d = doc.data();
  return {
    ...d,
    uid: doc.id,
    createdAt: ts(d.createdAt),
  } as UserProfile;
}

export function mapAllowlistEntryDoc(doc: AnyDoc): DriverAllowlistEntry {
  const d = doc.data();
  return {
    ...d,
    id: doc.id,
    createdAt: ts(d.createdAt),
  } as DriverAllowlistEntry;
}
